import {
  faAngleLeft,
  faAngleRight,
  faBullhorn,
  faDollarSign,
  faFileAlt,
  faTachometerAlt,
  faUsers,
  IconDefinition,
} from '@fortawesome/free-solid-svg-icons';
import {
  faBuilding,
  faCity,
  faClipboardList,
  faHandHeart,
} from '@fortawesome/pro-regular-svg-icons';
import { faBuildingUser, faGaugeHigh } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import { styled } from '@mui/material/styles';
import { history } from 'app';
import cx from 'classnames';
import GoLiveNotification from '../GoLive/GoLiveNotification';
import DisputesNotification from '../Disputes/DisputesNotification';
import NavbarBottomPart from 'new/components/AppBar/NavbarBottomPart';
import useActiveOffice from 'new/hooks/useActiveOffice';
import { useSidebarStore } from 'new/hooks/useSidebarStore';
import { truncate } from 'new/utils/strings';
import findRoute from 'new/routing/routes';
import { TENANT } from 'new/tenants';
import React, { useState } from 'react';
import { Link } from 'react-router';
import { useGetPlans } from 'new/modules/MembershipPlansManage/hooks';
import { useGetMyDentist } from './hooks';

const { SvgLogo, SvgLogoSmall, SvgLogoOnLight } = TENANT.logos;

export const SIDEBAR_EXPANDED_WIDTH = 230;
export const SIDEBAR_COLLAPSED_WIDTH = 70;
export const OLD_SIDEBAR_WIDTH = 215;
const LOGO_MAX_HEIGHT = 87;

// import Cookies from 'universal-cookie';
import { SVG } from 'new/types/common';
import { TenantStruct } from 'new/types/util';
import { BANNER_HEIGHT } from '../GlobalBanner';
import { CustomDrawer, CustomListItem } from './styles';
// const cookies = new Cookies();

const adminLogo: TenantStruct<SVG> = {
  vhq: SvgLogo,
  dhq: SvgLogoOnLight,
};

const logo: TenantStruct<SVG> = {
  vhq: SvgLogo,
  dhq: SvgLogo,
};

const AdminLogo = adminLogo[TENANT.id];
const Logo = logo[TENANT.id];

const LogoContainer = styled(ListItem)({
  position: 'relative',
  marginBottom: '25px',
  marginLeft: -2,
  height: LOGO_MAX_HEIGHT,
  display: 'flex',
  justifyContent: 'center',
  padding: 0,

  svg: {
    width: 'auto',

    '&.logoSmall': {
      height: 30,
      marginLeft: 12,
    },

    '&.logoLarge': {
      marginLeft: 0,
      height: 55,
    },
  },
});

export const CustomListItemText = styled(ListItemText)({
  transition: 'all 300ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',

  span: {
    color: '#ddd',
  },

  '&.collapsed': {
    transform: 'translateX(-2rem)',
    opacity: 0,
    whiteSpace: 'nowrap',
  },
});

const CustomIconButton = styled(IconButton, {
  shouldForwardProp: (prop) =>
    !['isSuperAdminSidebar', 'showBanner'].includes(prop as string),
})<{ isSuperAdminSidebar?: boolean; showBanner: boolean }>(
  ({ theme, isSuperAdminSidebar, showBanner }) => ({
    position: 'absolute',
    width: 25,
    height: 25,
    top: showBanner ? BANNER_HEIGHT + 31 : 31,
    right: -11,
    color: '#ddd',
    background: isSuperAdminSidebar
      ? theme.palette.misc.sidebarAdminBackground
      : theme.palette.misc.sidebarBackground,
    zIndex: 3,
    '&:hover': {
      backgroundColor: theme.palette.dhqGray.main,
    },
  }),
);

const setPoweredByStyles = (
  isExpanded: boolean,
  isHovered: boolean,
  color: string,
) => ({
  height: isExpanded || isHovered ? undefined : 0,
  opacity: isExpanded || isHovered ? 1 : 0,
  transition:
    isExpanded || isHovered ? 'opacity 0.3s ease-in' : 'opacity 0s ease-in',
  paddingTop: 4,
  color,
});

const StyledImgPatientLogo = styled('img', {
  shouldForwardProp: (prop) => prop !== 'isExpanded',
})<{ isExpanded: boolean; isHovered: boolean }>(
  ({ theme, isExpanded, isHovered }) => ({
    width: 'auto',
    maxWidth:
      isExpanded || isHovered
        ? SIDEBAR_EXPANDED_WIDTH
        : SIDEBAR_COLLAPSED_WIDTH,
    maxHeight: LOGO_MAX_HEIGHT - 16,
    marginTop: isExpanded || isHovered ? 16 : -16,
    padding: theme.spacing(1, 1, 0),
    transition:
      isExpanded || isHovered
        ? 'max-width 0.3s ease-in-out'
        : 'max-width 0.1s ease-in-out',

    '+ span': {
      marginLeft: theme.spacing(1),
      ...setPoweredByStyles(isExpanded, isHovered, theme.palette.grey[400]),
    },
  }),
);

const StyledPPatientLogo = styled('div', {
  shouldForwardProp: (prop) => prop !== 'isExpanded',
})<{ isExpanded: boolean; isHovered: boolean }>(
  ({ theme, isExpanded, isHovered }) => ({
    maxWidth:
      isExpanded || isHovered
        ? SIDEBAR_EXPANDED_WIDTH
        : SIDEBAR_COLLAPSED_WIDTH,
    maxHeight: LOGO_MAX_HEIGHT,
    color: '#fff',
    padding: theme.spacing(1),
    whiteSpace: !isExpanded && !isHovered ? 'nowrap' : undefined,
    overflow: 'hidden',
    textOverflow: !isExpanded && !isHovered ? 'ellipsis' : undefined,

    span: {
      display: 'block',
      ...setPoweredByStyles(isExpanded, isHovered, theme.palette.grey[400]),
    },
  }),
);

const dashboardPath = findRoute('AnalyticDashboardPage').path as string;
const membersPath = findRoute('MembersPage').path as string;
const businessConnectPath = findRoute('BusinessConnectPage').path as string;
const communityConnectPath = findRoute('CommunityConnectPage').path as string;
const marketingPage = findRoute('MarketingSpace').path as string;
const financialSolutionPath = findRoute('InhouseFinancing').path as string;
const reportsPath = findRoute('Reports').path as string;
const multiLocationDashboardPath = findRoute('MultiLocationDashboardPage')
  .path as string;
const manageLocationsPath = findRoute('ManageLocations').path as string;
const manageUsersPath = findRoute('ManageUsers').path as string;
const managePlansPath = findRoute('ManagePlans').path as string;
const manageGroupsPath = findRoute('ManageGroups').path as string;
const multiLocationBillingPath = findRoute('MultiLocationBilling')
  .path as string;
const multiLocationReportsPath = findRoute('MultiLocationReports')
  .path as string;
const patientDashboardPath = findRoute('PatientDetailsDashboardPage')
  .path as string;
const patientPaymentHistoryPath = findRoute('PatientDetailsPaymentHistoryPage')
  .path as string;

type Path = {
  text: string;
  path: string;
  icon: IconDefinition;
  disabled?: boolean;
  target?: string;
};

const Sidebar: React.FC = () => {
  const {
    isSuperAdmin,
    isPatient,
    user: data,
    activeOfficeId,
    isLoadingUser,
    isMultiLocation,
    globalBanner,
    isAdmin,
    refetch,
  } = useActiveOffice();
  const { data: plans } = useGetPlans(activeOfficeId);

  const upgradedOffice = data?.data.tieredPayment?.tierLevel != 0;
  const [isHovered, setIsHovered] = useState(false);
  const { isExpanded, toggleIsExpanded, viewAsStandard, tempViewAsMulti } =
    useSidebarStore();
  const { data: dataMyDentist } = useGetMyDentist(isPatient);

  const standardPaths: Path[] = [
    {
      text: 'Dashboard',
      path: dashboardPath,
      icon: faTachometerAlt,
    },
    {
      text: 'Members',
      path: membersPath,
      icon: faUsers,
    },
    ...(TENANT.features.businessConnect
      ? [
          {
            text: 'BusinessCONNECT',
            path: businessConnectPath,
            icon: faBuildingUser as IconDefinition,
          },
        ]
      : []),
    ...(isAdmin
      ? [
          {
            text: 'CommunityCONNECT',
            path: communityConnectPath,
            icon: faHandHeart as IconDefinition,
          },
        ]
      : []),
    ...(TENANT.features.inhouseFinancing
      ? [
          {
            text: 'In-House Financing',
            path: financialSolutionPath,
            icon: faDollarSign,
          },
        ]
      : []),
    {
      text: 'Reports',
      path: reportsPath,
      icon: faFileAlt,
    },
    {
      text: 'Marketing',
      path: marketingPage,
      icon: faBullhorn,
    },
  ];

  const showMulti = data?.data.useMulti;

  const isSuperAdminSidebar =
    isSuperAdmin &&
    isMultiLocation &&
    !viewAsStandard &&
    (tempViewAsMulti || showMulti);

  const isPatientSidebar = isPatient;

  const officeId = data?.data.daoi?.id;
  const currentOfficeInOtherOffices = data?.data.otherOffices?.find(
    (office) => office?.id === officeId,
  );
  const isAnyActivePlan = plans?.custom_memberships.some(
    (plan) => plan.viewStatus === 'active',
  );
  const isOfficeNotGoneLive =
    (data?.data.goneLive === false ||
      currentOfficeInOtherOffices?.goneLive === false) &&
    data?.data &&
    [3, 4, 5].indexOf(data?.data.daoi.accountStatus) == -1;

  const superAdminPaths: Path[] = [
    {
      text: 'Dashboard',
      path: multiLocationDashboardPath,
      icon: faGaugeHigh as IconDefinition,
    },
    {
      text: 'Manage Locations',
      path: manageLocationsPath,
      icon: faBuilding as IconDefinition,
    },
    {
      text: 'Manage Groups',
      path: manageGroupsPath,
      icon: faCity as IconDefinition,
    },
    {
      text: 'Manage Plans',
      path: managePlansPath,
      icon: faClipboardList as IconDefinition,
    },
    {
      text: 'Manage Users',
      path: manageUsersPath,
      icon: faUsers as IconDefinition,
    },
    {
      text: 'Billing',
      path: multiLocationBillingPath,
      icon: faDollarSign,
    },
    {
      text: 'Reports',
      path: multiLocationReportsPath,
      icon: faFileAlt,
    },
  ];

  const patientPaths: Path[] = [
    {
      text: 'Dashboard',
      path: patientDashboardPath,
      icon: faGaugeHigh as IconDefinition,
    },
    {
      text: 'Payment Info',
      path: patientPaymentHistoryPath,
      icon: faFileAlt as IconDefinition,
    },
  ];

  let paths = isSuperAdminSidebar ? superAdminPaths : standardPaths;
  if (isPatientSidebar) {
    paths = patientPaths;
  }
  if (isLoadingUser) {
    paths = [];
  }

  let timeout1: null | ReturnType<typeof setTimeout> = null;
  let timeout2: null | ReturnType<typeof setTimeout> = null;

  const delayedMouseOver = () => {
    timeout1 = setTimeout(handleMouseOver, 250);
    clearTimeout2();
  };
  const delayedMouseLeave = () => {
    timeout2 = setTimeout(handleMouseLeave, 250);
    clearTimeout1();
  };

  const handleMouseOver = () => {
    !isExpanded && setIsHovered(true);
    clearTimeout1();
  };
  const handleMouseLeave = () => {
    isHovered && setIsHovered(false);
    clearTimeout2();
  };

  const clearTimeout1 = () => {
    clearTimeout(Number(timeout1));
  };
  const clearTimeout2 = () => {
    clearTimeout(Number(timeout2));
  };

  const setPatientLogo = () => {
    const poweredBy = (
      <span style={{ fontSize: '.8rem' }}>Powered by {TENANT.name}</span>
    );

    if (dataMyDentist?.data?.logo) {
      return (
        <div
          style={{
            paddingTop: 8,
            display: 'flex',
            flexDirection: 'column',
            alignContent: 'center',
          }}
        >
          <StyledImgPatientLogo
            alt="logo"
            src={dataMyDentist?.data.logo}
            isExpanded={isExpanded}
            isHovered={isHovered}
          />
          {poweredBy}
        </div>
      );
    }

    if (dataMyDentist?.data?.dentistInfo.officeName) {
      return (
        <div>
          <StyledPPatientLogo isExpanded={isExpanded} isHovered={isHovered}>
            {truncate(dataMyDentist?.data?.dentistInfo.officeName, 50)}
            {poweredBy}
          </StyledPPatientLogo>
        </div>
      );
    }
  };

  return (
    <CustomDrawer
      variant="permanent"
      anchor="left"
      isSuperAdminSidebar={isSuperAdminSidebar}
      showBanner={globalBanner != null}
    >
      <CustomIconButton
        onClick={toggleIsExpanded}
        showBanner={globalBanner != null}
      >
        <FontAwesomeIcon
          icon={isExpanded || isHovered ? faAngleLeft : faAngleRight}
          size="xs"
        />
      </CustomIconButton>
      <Collapse
        orientation="horizontal"
        collapsedSize={SIDEBAR_COLLAPSED_WIDTH}
        sx={{
          height: '100%',
          overflow: 'hidden',
          width:
            isExpanded || isHovered
              ? SIDEBAR_EXPANDED_WIDTH
              : SIDEBAR_COLLAPSED_WIDTH,
        }}
        onMouseEnter={delayedMouseOver}
        onMouseLeave={delayedMouseLeave}
      >
        <LogoContainer>
          {isPatientSidebar ? (
            setPatientLogo()
          ) : (
            <>
              {!isSuperAdminSidebar && (
                <Logo
                  className="logoLarge"
                  style={{
                    visibility: isExpanded || isHovered ? 'visible' : 'hidden',
                  }}
                />
              )}
              {isSuperAdminSidebar && (
                <AdminLogo
                  className="logoLarge"
                  style={{
                    visibility: isExpanded || isHovered ? 'visible' : 'hidden',
                  }}
                />
              )}
              <SvgLogoSmall
                className="logoSmall"
                style={{
                  position: 'absolute',
                  zIndex: 4,
                  left: isExpanded || isHovered ? -100 : 0,
                }}
              />
            </>
          )}
        </LogoContainer>
        <List>
          {paths.map((item) => (
            <Link
              to={
                !upgradedOffice
                  ? '/dentist-new/marketing'
                  : item.disabled
                  ? '/'
                  : item.path
              }
              key={item.text}
              onClick={
                !upgradedOffice || item.disabled
                  ? (e) => e.preventDefault()
                  : undefined
              }
              style={
                !upgradedOffice || item.disabled
                  ? { pointerEvents: 'none', cursor: 'default' }
                  : undefined
              }
              target={item.target}
              disabled={!upgradedOffice || item.disabled}
            >
              <CustomListItem
                className={cx({
                  disabled: !upgradedOffice || item.disabled,
                  isSuperAdmin: isSuperAdmin && !viewAsStandard,
                  isSuperAdminSidebar,
                })}
                selected={history
                  .getCurrentLocation()
                  .pathname.includes(item.path)}
              >
                <Box className="listItemIcon" sx={{ margin: '2px 0' }}>
                  <FontAwesomeIcon icon={item.icon} />
                </Box>
                <CustomListItemText
                  primary={item.text}
                  className={isExpanded || isHovered ? '' : 'collapsed'}
                />
              </CustomListItem>
            </Link>
          ))}
        </List>
        {isOfficeNotGoneLive &&
          isAnyActivePlan &&
          isSuperAdminSidebar === false && (
            <GoLiveNotification
              isExpanded={isExpanded}
              isHovered={isHovered}
              refetch={refetch}
            />
          )}
        <DisputesNotification isExpanded={isExpanded} isHovered={isHovered} />
        <NavbarBottomPart
          isExpanded={isExpanded}
          isHovered={isHovered}
          isSuperAdminSidebar={isSuperAdminSidebar}
        />
      </Collapse>
    </CustomDrawer>
  );
};

export default Sidebar;
